import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  groupPromptCommandsByCategory,
  type PromptCommand,
  type PromptCommandGroup,
} from '../../../../lib/slash-command-constants';

interface UseSlashCommandListStateOptions {
  commands: PromptCommand[];
  searchQuery: string;
  isOpen: boolean;
  onSelect: (command: PromptCommand) => void;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
}

/**
 * Shared hook for managing slash command list state including:
 * - Filtering commands based on search query
 * - Grouping commands by category
 * - Keyboard navigation
 * - Expanded command state
 */
export function useSlashCommandListState({
  commands,
  searchQuery,
  isOpen,
  onSelect,
  onClose,
  inputRef,
}: UseSlashCommandListStateOptions) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedCommandUuid, setExpandedCommandUuid] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Filter commands based on search query
  const filteredCommands = useMemo(() => {
    if (!searchQuery || searchQuery === '/') {
      return commands;
    }

    const normalizedQuery = searchQuery.toLowerCase().replace(/^\//, '');
    return commands.filter(
      (command) =>
        command.handle.toLowerCase().includes(normalizedQuery) ||
        command.description.toLowerCase().includes(normalizedQuery) ||
        command.title?.toLowerCase().includes(normalizedQuery),
    );
  }, [commands, searchQuery]);

  // Group filtered commands by category
  const groupedCommands = useMemo<PromptCommandGroup[]>(
    () => groupPromptCommandsByCategory(filteredCommands),
    [filteredCommands],
  );

  // Flatten commands for keyboard navigation
  const flatCommands = useMemo(() => filteredCommands, [filteredCommands]);

  // Reset selected index and collapse expanded command when filtered commands change
  useEffect(() => {
    setSelectedIndex(0);
    setExpandedCommandUuid(null);
  }, [filteredCommands.length]);

  // Toggle expanded state for a command
  const toggleExpanded = useCallback((commandUuid: string) => {
    setExpandedCommandUuid((prev) => (prev === commandUuid ? null : commandUuid));
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    const selectedItem = itemRefs.current.get(selectedIndex);
    if (selectedItem && listRef.current) {
      selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % flatCommands.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + flatCommands.length) % flatCommands.length);
          break;
        case 'Tab':
        case 'Enter':
          event.preventDefault();
          if (flatCommands[selectedIndex]) {
            onSelect(flatCommands[selectedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    },
    [isOpen, flatCommands, selectedIndex, onSelect, onClose],
  );

  // Attach keyboard listener to editor element
  useEffect(() => {
    const editor = inputRef?.current;
    if (!editor || !isOpen) return;

    editor.addEventListener('keydown', handleKeyDown as EventListener);
    return () => {
      editor.removeEventListener('keydown', handleKeyDown as EventListener);
    };
  }, [inputRef, isOpen, handleKeyDown]);

  // Register item ref for keyboard navigation
  const registerItemRef = useCallback((index: number, el: HTMLButtonElement | null) => {
    if (el) {
      itemRefs.current.set(index, el);
    } else {
      itemRefs.current.delete(index);
    }
  }, []);

  return {
    filteredCommands,
    groupedCommands,
    selectedIndex,
    expandedCommandUuid,
    listRef,
    toggleExpanded,
    registerItemRef,
  };
}
